import { FC, Fragment, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DescriptionList } from "./DescriptionList";

type RawProviderSection = Record<string, unknown>;

const isRawProviderSection = (value: unknown): value is RawProviderSection => {
	return value != null && typeof value === "object";
};

type RawProviderValue = number | string | boolean | null;

const isRawProviderValue = (value: unknown): value is RawProviderValue => {
	return value === null || typeof value === "number" || typeof value === "string" || typeof value === "boolean";
};

const StyledSection = styled.section`
	& + & {
		margin-top: ${({ theme }) => theme.spacing(6)};
	}
`;

const SectionHeadline = styled.strong`
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
`;

const StyledSectionContent = styled.div`
	padding-left: ${({ theme }) => theme.spacing(6)};
	border-left: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
`;

interface SectionProps {
	label: string;
	section: RawProviderSection;
}

const Section: FC<SectionProps> = ({ label, section }) => {
	const labelId = useId();
	const values = useMemo(
		() => Object.entries(section).filter(([, value]) => isRawProviderValue(value)) as [[string, RawProviderValue]],
		[section],
	);
	const sections = useMemo(
		() => Object.entries(section).filter(([, value]) => isRawProviderSection(value)) as [[string, RawProviderSection]],
		[section],
	);

	return (
		<StyledSection aria-labelledby={labelId}>
			<SectionHeadline id={labelId}>{label}</SectionHeadline>
			<StyledSectionContent>
				<DescriptionList>
					{values.map(([label, value]) => (
						<Fragment key={label}>
							<dt>{label}</dt>
							<dd>{value == null || value == "" ? "-" : String(value)}</dd>
						</Fragment>
					))}
				</DescriptionList>
				{sections.map(([label, value]) => (
					<Section key={label} label={label} section={value} />
				))}
			</StyledSectionContent>
		</StyledSection>
	);
};

interface Props {
	rawProviderData: Record<string, unknown>;
}

export const RawProviderData: FC<Props> = ({ rawProviderData }) => {
	const { t } = useTranslation();

	return <Section label={t("Provider data")} section={rawProviderData} />;
};
